// T00VAMP99KWR — the ES2023 "change array by copy" methods for browsers that
// lack them (Safari before 16, i.e. older iPhones and iPads). Vuetify's
// VSnackbarQueue calls toReversed() for every toast; without it the toast
// host crashes there. Vite only transpiles syntax, it never adds missing
// built-ins, so they come from here. Native implementations are left alone.

type ArrayMethods = {
  toReversed: <T>(this: T[]) => T[];
  toSorted: <T>(this: T[], compareFn?: (a: T, b: T) => number) => T[];
  toSpliced: <T>(this: T[], start?: number, skipCount?: number, ...items: T[]) => T[];
  with: <T>(this: T[], index: number, value: T) => T[];
};

const methods: ArrayMethods = {
  toReversed<T>(this: T[]): T[] {
    return Array.from(this).reverse();
  },
  toSorted<T>(this: T[], compareFn?: (a: T, b: T) => number): T[] {
    if (compareFn !== undefined && typeof compareFn !== 'function') {
      throw new TypeError('The comparison function must be either a function or undefined');
    }
    return Array.from(this).sort(compareFn);
  },
  toSpliced<T>(this: T[], ...args: [start?: number, skipCount?: number, ...items: T[]]): T[] {
    const copy = Array.from(this);
    // splice() with no arguments removes nothing, with only a start everything after it.
    (copy.splice as (...spliceArgs: unknown[]) => T[])(...args);
    return copy;
  },
  with<T>(this: T[], index: number, value: T): T[] {
    const length = this.length;
    const relative = Math.trunc(Number(index)) || 0;
    const actual = relative < 0 ? length + relative : relative;
    if (actual < 0 || actual >= length) {
      throw new RangeError('Invalid index');
    }
    const copy = Array.from(this);
    copy[actual] = value;
    return copy;
  },
};

export function installArrayChangeByCopy(): void {
  for (const [name, implementation] of Object.entries(methods)) {
    if (typeof (Array.prototype as unknown as Record<string, unknown>)[name] !== 'function') {
      // Like the native ones: writable, configurable, not enumerable.
      Object.defineProperty(Array.prototype, name, {value: implementation, writable: true, configurable: true, enumerable: false});
    }
  }
}

installArrayChangeByCopy();
